import React, { useEffect, useMemo, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import Moment from 'moment/moment';

import { getBillingRecordHistory } from '../../services/billingRecords';
import { BillingRecordHistoryEntry } from '../../interfaces/billing.interface';

const FIELD_LABELS: Record<string, string> = {
  billing_frequency: 'Billing Frequency',
  status: 'Status',
  inactive_reason: 'Inactive Reason',
  detail_for_cancellation: 'Detail for Cancellation',
  most_recent_cancellation_date: 'Most Recent Cancellation Date',
  most_recent_rejoin_date: 'Most Recent Rejoin Date',
  notes: 'Notes',
};

const HISTORY_TYPE_LABELS: Record<string, string> = {
  '+': 'Created',
  '~': 'Updated',
  '-': 'Deleted',
};

export function humanizeField(field: string): string {
  return FIELD_LABELS[field] ?? field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// Imported rows carry no history_user; the person's name is embedded in the change reason marker.
export function parseImportedName(changeReason: string | null): string | null {
  if (!changeReason) return null;
  const match = changeReason.match(/^Imported from Monday\s+.*?\s+-\s+(.+)$/);
  return match ? match[1].trim() : null;
}

function toInitials(source: string): string {
  const parts = source.replace(/@.*/, '').split(/[.\s_-]+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '');
  return letters.join('') || source[0]?.toUpperCase() || '?';
}

function displayValue(value: string | null): string {
  return value === null || value === '' ? '—' : value;
}

interface InvoiceHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  billingRecordId: number;
  companyName: string;
}

export default function InvoiceHistoryDrawer({ open, onClose, billingRecordId, companyName }: InvoiceHistoryDrawerProps) {
  const [entries, setEntries] = useState<BillingRecordHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [selectedField, setSelectedField] = useState<string>('all');

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    setError(false);
    setEntries([]);
    setSelectedField('all');
    getBillingRecordHistory(billingRecordId)
      .then((data) => { if (active) setEntries(data); })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, billingRecordId]);

  const availableFields = useMemo(() => {
    const fields = new Set<string>();
    entries.forEach((entry) => entry.changes.forEach((change) => fields.add(change.field)));
    return Array.from(fields).sort((a, b) => humanizeField(a).localeCompare(humanizeField(b)));
  }, [entries]);

  const filteredEntries = selectedField === 'all'
    ? entries
    : entries.filter((entry) => entry.changes.some((change) => change.field === selectedField));

  return (
    <Drawer anchor="right" open={open} onClose={onClose} sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
      <Box sx={{ width: 460, maxWidth: '100vw', p: 3 }} role="region" aria-label="Invoice History">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6">Invoice History</Typography>
            <Typography variant="body2" color="text.secondary">{companyName}</Typography>
          </Box>
          <IconButton aria-label="Close invoice history" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <FormControl fullWidth size="small" sx={{ mt: 3, mb: 2 }}>
          <InputLabel id="invoice-history-field-filter-label">Filter by field</InputLabel>
          <Select
            labelId="invoice-history-field-filter-label"
            label="Filter by field"
            value={selectedField}
            onChange={(e) => setSelectedField(e.target.value)}
            MenuProps={{ sx: { zIndex: (theme) => theme.zIndex.modal + 2 } }}
          >
            <MenuItem value="all">All</MenuItem>
            {availableFields.map((field) => (
              <MenuItem key={field} value={field}>{humanizeField(field)}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider sx={{ mb: 2 }} />

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Typography color="error" variant="body2">Unable to load the change history. Please try again.</Typography>
        )}

        {!loading && !error && filteredEntries.length === 0 && (
          <Typography variant="body2" color="text.secondary">No change history to display.</Typography>
        )}

        {!loading && !error && filteredEntries.map((entry) => {
          const isImported = !entry.historyUser && !!entry.historyChangeReason?.startsWith('Imported from Monday');
          const actor = isImported
            ? (parseImportedName(entry.historyChangeReason) ?? 'Finance Tracker')
            : (entry.historyUser ?? 'System');
          const accent = isImported ? 'warning.main' : 'primary.main';
          const visibleChanges = selectedField === 'all'
            ? entry.changes
            : entry.changes.filter((change) => change.field === selectedField);

          return (
            <Box key={entry.historyId} sx={{ borderLeft: 4, borderColor: accent, pl: 2, py: 1.5, mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: accent }}>{toInitials(actor)}</Avatar>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{actor}</Typography>
                {isImported && (
                  <Chip size="small" color="warning" variant="outlined" label="via Finance Tracker" />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {HISTORY_TYPE_LABELS[entry.historyType] ?? entry.historyType}
                {' · '}
                {Moment(entry.historyDate).format('M/D/YYYY h:mm A')}
              </Typography>
              <Box sx={{ mt: 1 }}>
                {visibleChanges.length === 0 && (
                  <Typography variant="body2" color="text.secondary">No field changes recorded.</Typography>
                )}
                {visibleChanges.map((change) => (
                  <Typography key={change.field} variant="body2" sx={{ mb: 0.5 }}>
                    <Box component="span" sx={{ fontWeight: 600 }}>{humanizeField(change.field)}: </Box>
                    {displayValue(change.old)} → {displayValue(change.new)}
                  </Typography>
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Drawer>
  );
}
